/* eslint-disable camelcase */
/* eslint-disable spire/fenced-imports */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Suggestion } from "@coveo/headless/commerce";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Clock from "@insite/mobius/Icons/Clock";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useCoveoSearch from "@NA/Components/Coveo/Commerce/useCoveoSearch";
import React from "react";
import { css } from "styled-components";
import ADIColors from "@NA/Utils/ADIColors";
import { ButtonIcon } from "@insite/mobius/Button";
import SearchIcon from "@NA/Icons/SearchIcon";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { StyledButton } from "@insite/mobius/Clickable/Clickable";
import { HasCoveoHooks, withCoveoHooks } from "@NA/Components/Coveo/Contexts/CoveoHooksContext";

interface OwnProps {
    clearSearch: () => void;
    closeAutocomplete: () => void;
    extendedStyles?: SearchHistoryStyles;
}

type Props = OwnProps & HasCoveoHooks;

export interface SearchHistoryStyles {
    headerText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    icon?: IconPresentationProps;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
    clearLink?: LinkPresentationProps;
    iconSearch?: InjectableCss;
}

export const searchHistoryStyles: SearchHistoryStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 0;
            font-size: 16px;
            font-style: normal;
            color: ${ADIColors.darkBlack3}!important;
            line-height: normal;
            padding-bottom: 10px;
            @media (min-width: 360px) and (max-width: 1079px) {
                padding-top: 0;
            }
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 50%;
            padding: 0 15px;
            @media (min-width: 360px) and (max-width: 1079px) {
                padding: 0;
            }
            @media (min-width: 360px) and (max-width: 767px) {
                width: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        border-bottom: 1px solid ${ADIColors.gray15};
                        padding: 0px 0px 20px 0px;
                        margin-bottom: 5px;
                    `,
                    css`
                        border-bottom: 1px solid ${ADIColors.gray15};
                        padding: 0px 0px 20px 0px;
                        margin-bottom: 5px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    itemWrapper: {
        css: css`
            display: flex;
            align-items: center;
            ${StyledButton} {
                width: auto;
            }
        `,
    },
    icon: {
        src: Clock,
        css: css`
            margin-right: 7px;
        `,
    },
    link: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                font-size: 14px;
                font-weight: 700;
                padding-bottom: 10px;
                margin: 0px;
                color: ${ADIColors.darkBlack3}!important;
                line-height: normal;
                &:hover {
                    text-decoration: underline !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding-bottom: 15px;
                        `,
                        css`
                            padding-bottom: 15px;
                        `,
                        css`
                            padding-bottom: 15px;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
        css: css`
            width: 100%;
            overflow: hidden;
            margin-bottom: 0;
            font-size: 14px;
            font-weight: 700;
            margin: 0px;
            color: rgb(45, 41, 38) !important;
            line-height: normal;
            &:hover {
                text-decoration: underline !important;
            }
        `,
    },
    focusedLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            width: 100%;
            overflow: hidden;
            background-color: lightgray;
        `,
    },
    clearLink: {
        typographyProps: {
            css: css`
                width: 100%;
                text-align: right;
                margin-top: 10px;
            `,
        },
    },
    iconSearch: {
        css: css`
            margin-right: 7px;
            margin-bottom: 0px;
            top: -3.5px;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        top: -5px;
                    `,
                    css`
                        top: -6px;
                    `,
                    css`
                        top: -6px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const CoveoSearchSuggestions = ({ clearSearch, closeAutocomplete, extendedStyles, searchBox }: Props) => {
    const styles = mergeToNew(searchHistoryStyles, extendedStyles);

    const { doCoveoSearch } = useCoveoSearch();

    const {state: searchBoxState} = searchBox;

    const generateKey = (index: number) => {
        return `querySuggestions_${index}`;
    };

    if (!searchBoxState?.suggestions || searchBoxState.suggestions.length == 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.headerText}>{translate("Search Suggestions")}</Typography>
            {searchBoxState?.suggestions.map((suggestion: Suggestion, index: number) => (
                <StyledWrapper {...styles.itemWrapper} key={generateKey(index)}>
                    <ButtonIcon color={ADIColors.darkBlack3} size={12} src={SearchIcon} {...styles.iconSearch} />
                    <Link
                        {...styles.link}
                        onClick={() => {
                            doCoveoSearch(suggestion.rawValue.replaceAll(" ", "+"));
                            clearSearch();
                            closeAutocomplete();
                        }}
                    >
                        <Typography dangerouslySetInnerHTML={{ __html: suggestion.highlightedValue }} />
                    </Link>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

export default withCoveoHooks(CoveoSearchSuggestions);
